import React, { useState, useEffect, useRef } from 'react';
import './HomePage.css';

function HomePage() {
  const [showModal, setShowModal] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const featuresRef = useRef(null);

  useEffect(() => {
    // Handle scroll events for navbar
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active nav based on scroll position
      const sections = ['hero', 'features', 'about', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveNav(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateToDashboard = () => {
    window.location.hash = '#dashboard';
    window.dispatchEvent(new Event('hashchange'));
  };

  const scrollToSection = (sectionId) => {
    setActiveNav(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="homepage">
      {/* Professional Navigation Bar */}
      <nav className={`home-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="home-nav-container">
          <div className="home-logo" onClick={() => scrollToSection('hero')}>
            <div className="logo-icon-wrapper">
              <span className="home-logo-icon">🛡️</span>
            </div>
            <span className="home-logo-text">PolicyEye</span>
          </div>
          <ul className="home-nav-menu">
            <li className={activeNav === 'hero' ? 'home-active' : ''} onClick={() => scrollToSection('hero')}>
              Home
            </li>
            <li className={activeNav === 'features' ? 'home-active' : ''} onClick={() => scrollToSection('features')}>
              Features
            </li>
            <li className={activeNav === 'about' ? 'home-active' : ''} onClick={() => scrollToSection('about')}>
              About
            </li>
            <li className={activeNav === 'contact' ? 'home-active' : ''} onClick={() => scrollToSection('contact')}>
              Contact
            </li>
          </ul>
          <button className="nav-dashboard-btn" onClick={navigateToDashboard}>
            Dashboard
            <span className="btn-arrow">→</span>
          </button>
          <div className="home-menu-icon" onClick={() => {
            const menu = document.querySelector('.home-nav-menu');
            menu.classList.toggle('home-active-menu');
          }}>
            ☰
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon"></span>
              <span>Industry Leading Safety Platform</span>
            </div>
            <h1 className="hero-title">
              Best Practices For All Business
              <span className="title-highlight"> Secure Systems</span>
            </h1>
            <p className="hero-description">
              Transform your safety documents into actionable insights. Our AI-powered platform helps businesses 
              automate compliance, generate training videos, and maintain world-class safety standards.
            </p>
            <div className="hero-buttons">
              <button className="btn-primary" onClick={navigateToDashboard}>
                Get Started 
                <span className="btn-icon">→</span>
              </button>
              <button className="btn-outline" onClick={() => setShowModal(true)}>
                Watch Demo
                <span className="btn-icon">▶</span>
              </button>
            </div>
            <div className="hero-stats">
              
              
              
              
            </div>
          </div>
          <div className="hero-image-wrapper">
            <div className="hero-image-container">
              <img 
                src="/images/img1.png" 
                alt="Security Systems" 
                className="hero-img"
                onError={(e) => {
                  e.target.onerror = null;
                  
                }}
              />
              <div className="image-overlay"></div>
            </div>
            <div className="floating-card card-1">
              <span className="card-icon">📊</span>
              <div>
                <div className="card-title">Real-time Analytics</div>
                <div className="card-value">Active Monitoring</div>
              </div>
            </div>
            <div className="floating-card card-2">
              <span className="card-icon">🎥</span>
              <div>
                <div className="card-title">Video Generation</div>
                <div className="card-value">AI Powered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section" ref={featuresRef}>
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Why Choose Us</div>
            <h2 className="section-title">Powerful Features for Modern Safety Management</h2>
            <p className="section-subtitle">Everything you need to transform your safety documentation into an interactive experience</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">📄</div>
              </div>
              <h3>Smart PDF Upload</h3>
              <p>Upload your safety documents and let our AI extract, categorize, and analyze every rule automatically.</p>
              <div className="feature-link">Learn more →</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">🎥</div>
              </div>
              <h3>AI Video Generation</h3>
              <p>Create personalized safety training videos instantly based on your extracted rules and procedures.</p>
              <div className="feature-link">Learn more →</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">📊</div>
              </div>
              <h3>Comprehensive Reports</h3>
              <p>Generate detailed PDF reports and CSV exports for compliance tracking and audit purposes.</p>
              <div className="feature-link">Learn more →</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">🔒</div>
              </div>
              <h3>Real-time Monitoring</h3>
              <p>Track safety compliance metrics and receive instant alerts for critical issues.</p>
              <div className="feature-link">Learn more →</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">📱</div>
              </div>
              <h3>Mobile Access</h3>
              <p>Access your safety data from anywhere with our fully responsive mobile platform.</p>
              <div className="feature-link">Learn more →</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <div className="feature-icon">🤖</div>
              </div>
              <h3>AI Analytics</h3>
              <p>Get intelligent insights and recommendations based on your safety data patterns.</p>
              <div className="feature-link">Learn more →</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <div className="section-badge">About PolicyEye</div>
              <h2 className="section-title">Revolutionizing Safety Management with AI</h2>
              <p>SPolicyEye is a cutting-edge platform that leverages artificial intelligence to help businesses maintain the highest safety standards. Our mission is to make safety compliance simple, efficient, and engaging for organizations of all sizes.</p>
              <div className="about-features">
                <div className="about-feature">
                  <span className="about-check">✓</span>
                  <span>Enterprise-grade security</span>
                </div>
                <div className="about-feature">
                </div>
                <div className="about-feature">
                  <span className="about-check">✓</span>
                  <span>Regular updates & improvements</span>
                </div>
              </div>
              <button className="btn-secondary-about" onClick={navigateToDashboard}>
                Start Now
              </button>
            </div>
            <div className="about-image-wrapper">
              <div className="about-image-container">
                <img 
                  src="/images/about.jpg" 
                  alt="About SecureGuard"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=500&h=400&fit=crop';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="section-badge">Get in Touch</div>
              <h2 className="section-title">Ready to Transform Your Safety Management?</h2>
              <p>Join thousands of businesses that trust PolicyEye for their safety compliance needs.</p>
              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-detail-icon">📧</div>
                  <div>
                    <h4>Email Us</h4>
                    <p>support@secureguard.com</p>
                  </div>
                </div>
                <div className="contact-detail">
                  <div className="contact-detail-icon">📞</div>
                  <div>
                    <h4>Call Us</h4>
                    <p>+1 (555) 123-4567</p>
                  </div>
                </div>
                <div className="contact-detail">
                  <div className="contact-detail-icon">📍</div>
                  <div>
                    <h4>Visit Us</h4>
                    <p>123 Security Street, Business District</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form-wrapper">
              <form className="contact-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <input type="text" placeholder="Full Name" className="form-input" />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Email Address" className="form-input" />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="Company Name" className="form-input" />
                </div>
                <div className="form-group">
                  <textarea placeholder="How can we help you?" className="form-textarea" rows="4"></textarea>
                </div>
                <button type="submit" className="form-submit">Send Message →</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">🛡️ PolicyEye</div>
              <p>Making safety compliance simple, efficient, and engaging for businesses worldwide.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li>Features</li>
                  <li>Pricing</li>
                  <li>Integrations</li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li>About Us</li>
                  <li>Careers</li>
                  <li>Contact</li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Resources</h4>
                <ul>
                  <li>Documentation</li>
                  <li>Support</li>
                  <li>Blog</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; PolicyEye. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Why Safety Management is Crucial</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="importance-points">
                <div className="point">
                  <h3>🏢 Legal Compliance</h3>
                  <p>Avoid costly penalties and lawsuits by maintaining compliance with safety regulations.</p>
                </div>
                <div className="point">
                  <h3>👥 Employee Protection</h3>
                  <p>Create a safe working environment that protects your most valuable asset.</p>
                </div>
                <div className="point">
                  <h3>💰 Cost Reduction</h3>
                  <p>Reduce workplace accidents, insurance premiums, and operational disruptions.</p>
                </div>
                <div className="point">
                  <h3>📈 Business Continuity</h3>
                  <p>Maintain operations without interruptions caused by safety incidents.</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn" onClick={navigateToDashboard}>Get Started Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;